/**
 * SPIKE, stage 1 (owner, Sep 10: *"the terrain should have elevations. some areas also mountains. cities should be
 * mostly flat and only a little variations"* — and *"is there a way to cheat at the height thing?"*).
 *
 * Yes, and this is the cheat, which is also what nearly every 2.5D game does: **the world stays a height field, not a
 * 3D model.** A tile has one number, the ground's height in metres. Nothing is modelled in three dimensions, nothing
 * is meshed; the height only ever does three things:
 *
 *   1. it shifts what is drawn at that tile *up the screen* (the game already does exactly this for bridges, roofs
 *      and aircraft — `z` is drawn as a y-offset, so terrain height is simply another source of `z`);
 *   2. it shades the ground — a slope lit from one side is what makes a picture read as a hill, far more than the
 *      offset does;
 *   3. it costs a car speed uphill and hides what is behind a ridge.
 *
 * The second cheat is **terracing**. Heights are quantised to `TERRACE` metres, so ground is flat in benches with a
 * visible step between them, instead of a continuous slope that has to be resolved per pixel. It is cheaper to draw
 * (a step is a line, not a gradient), far cheaper for physics (a tile is flat, so collision stays 2D), and easier for
 * roads (a road runs along a bench and ramps between them). It also looks *deliberate* — cut into the land, the way
 * real hillside towns are — rather than mushy. Continuous height looks better only in a renderer that can actually
 * shade a slope per pixel, which this one, drawing sprites, cannot.
 */
import type { Fields } from './fields';
import { fbm } from './noise';

/** metres per bench: the ground is flat within one, and steps between them */
export const TERRACE = 2;
/**
 * How tall the ordinary rolling country gets, in metres. The elevation noise turns over about every 180 tiles, so
 * this number divided by ~90 is the gradient of the open country: 46 m gave a 12 % average, which is a mountain road
 * everywhere and nothing left for the mountains to be. 26 m is countryside you can drive across.
 */
const RELIEF = 26;
/** and the mountains, where there are any */
const MOUNTAIN = 260;
/** the share of the world that is mountainous at all (the mask's threshold: higher = rarer) */
const MOUNTAIN_AT = 0.62;
/** how flat a town is: 0 = the land as it lies, 1 = a table */
const TOWN_FLAT = 0.88;

export class Relief {
  private benches = new Map<string, number>();
  /**
   * Height per tile, memoised. This is not an optimisation, it is the difference between shipping and not: the first
   * chunk of a fresh area took **771 ms** without it, because a road's cost asks for the grade, the grade asks for
   * four heights, each height walks the settlements and evaluates several octaves of noise — and the candidate curves
   * ask thousands of times over the same few hundred tiles. Cached per tile it is ~20× fewer evaluations, and the
   * owner's rule is that this has to be smooth on a normal phone.
   */
  private tiles = new Map<number, number>();
  constructor(readonly fields: Fields, readonly seed: number) {}

  /**
   * The height a town's ground is cut to. Not the height at its centre — that is one sample of noise, and two
   * neighbouring towns landing on different samples left a 15 % grade in the blend between them. The bench is the
   * land *averaged over the town*, which is both what a real settlement sits on and what makes neighbouring towns
   * agree with each other, since they average overlapping ground.
   */
  private bench(s: { x: number; y: number; radius: number }): number {
    const key = `${s.x},${s.y}`;
    let b = this.benches.get(key);
    if (b !== undefined) return b;
    const r = s.radius * 0.6;
    b = this.raw(s.x, s.y);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      b += this.raw(s.x + Math.cos(a) * r, s.y + Math.sin(a) * r);
    }
    b /= 7;
    this.benches.set(key, b);
    return b;
  }

  /** the mountain mask, 0 in ordinary country and rising towards 1 in a range */
  mountainMask(x: number, y: number): number {
    const m = fbm(this.seed + 601, x / 1600, y / 1600, 3);
    if (m <= MOUNTAIN_AT) return 0;
    const t = (m - MOUNTAIN_AT) / (1 - MOUNTAIN_AT);
    return t * t; // squared, so a range has foothills rather than a wall
  }

  /**
   * The elevation this height is built on. `Fields.elevation` blurs 5×5 tiles to keep shorelines clean, which costs
   * **25 noise evaluations** and measured 10.75 µs a call — fine for deciding where water goes, ruinous for anything
   * that asks in bulk. Routing asks tens of thousands of times, so it takes the single unblurred sample instead: over
   * eight tiles the blur is invisible, and a road does not care about a one-tile pond it will never see.
   */
  private elev(x: number, y: number, fast: boolean): number {
    return fast ? this.fields.elevationRaw(x, y) : this.fields.elevation(x, y);
  }

  /** Height in metres before towns are levelled and before terracing — the shape of the land itself. */
  raw(x: number, y: number, fast = false): number {
    const f = this.fields;
    const e = this.elev(x, y, fast);
    // sea level is 0 m: below it is water, and the game keeps deciding that from the same field it always did
    const above = Math.max(0, e - f.seaLevel) / Math.max(0.001, 1 - f.seaLevel);
    const hills = above * RELIEF;
    const mask = this.mountainMask(x, y);
    if (mask <= 0) return hills;
    // ridges: a second, sharper noise inside the mask, so a range has peaks and saddles instead of a dome
    const ridge = 1 - Math.abs(fbm(this.seed + 607, x / 420, y / 420, 4) - 0.5) * 2;
    return hills + mask * MOUNTAIN * (0.45 + 0.55 * ridge * ridge);
  }

  /**
   * A town sits on a bench of its own: the height at its centre, blended over the town's radius, so streets are
   * nearly level and the land rises outside them (owner: "cities should be mostly flat and only a little variations").
   * The blend is smoothstepped, so the edge of town is a slope, not a cliff around the whole place.
   */
  private tilts = new Map<string, number>();
  /** a town's tilt, once per town: it was an `fbm` per town per sample, and the path net's cost grids sample the relief by the ten thousand (§28) */
  private tilt(s: { x: number; y: number }): number {
    const k = `${s.x},${s.y}`;
    let t = this.tilts.get(k);
    if (t === undefined) { t = (fbm(this.seed + 613, s.x / 900, s.y / 900, 2) - 0.5) * 5; this.tilts.set(k, t); }
    return t;
  }

  private townLevel(x: number, y: number, h: number): number {
    // One weighted average, not a chain of blends. Seven towns overlap around here, and blending them one after the
    // other left every one of them pulling the ground a different way — the "flat" town still fell three to eight
    // metres across a block. Averaged by weight, a place inside one town sits on that town's bench, and the ground
    // between two towns slides from one bench to the other.
    let wSum = 0, hSum = 0;
    for (const s of this.fields.settlements.near(x, y)) {
      const d = Math.hypot(x - s.x, y - s.y) / s.radius;
      if (d >= 1.7) continue;
      const t = Math.max(0, Math.min(1, 1 - d / 1.7));
      const w = t * t * (3 - 2 * t) * TOWN_FLAT;
      // a town is not a table: a couple of metres of fall across it, always the same way, is what makes it a place
      const tilt = this.tilt(s);
      hSum += w * (this.bench(s) + tilt * (1 - t));
      wSum += w;
    }
    if (wSum <= 0) return h;
    if (wSum >= 1) return hSum / wSum;
    return h * (1 - wSum) + hSum;
  }

  /** The height the game uses: metres, levelled under towns, quantised to benches. */
  height(x: number, y: number): number {
    const h = this.townLevel(x, y, this.raw(x, y));
    return Math.round(h / TERRACE) * TERRACE;
  }

  /** Height without the terracing — for anything that wants a smooth value (shading, a road's ramp). */
  smooth(x: number, y: number, fast = false): number {
    if (fast) return this.townLevel(x, y, this.raw(x, y, true));
    // per tile, because everything that asks for height asks in clusters and the ground does not change within a metre
    const tx = Math.floor(x), ty = Math.floor(y);
    const key = (tx & 0x3fffff) * 0x400000 + (ty & 0x3fffff);
    let h = this.tiles.get(key);
    if (h === undefined) {
      if (this.tiles.size > 400000) this.tiles.clear();
      h = this.townLevel(tx + 0.5, ty + 0.5, this.raw(tx + 0.5, ty + 0.5));
      this.tiles.set(key, h);
    }
    return h;
  }

  /** Metres of fall per tile, from the smooth height — what a car feels and what the shading uses. */
  grade(x: number, y: number, d = 3): { gx: number; gy: number; slope: number } {
    const gx = (this.smooth(x + d, y) - this.smooth(x - d, y)) / (2 * d);
    const gy = (this.smooth(x, y + d) - this.smooth(x, y - d)) / (2 * d);
    return { gx, gy, slope: Math.hypot(gx, gy) };
  }

  /** Under water, asked the cheap way — one noise sample, for the bulk callers that cannot afford the blur. */
  wetFast(x: number, y: number): boolean { return this.fields.elevationRaw(x, y) < this.fields.seaLevel; }

  /** Is there a step between this tile and the next one east or south? (the line a terrace is drawn with) */
  stepAt(x: number, y: number): boolean {
    const h = this.height(x, y);
    return this.height(x + 1, y) !== h || this.height(x, y + 1) !== h;
  }
}
